import * as moment from 'moment';

/**
 * Status-filter helpers for the "coding list" views (by-date, by-patient, and the
 * patient-file coding list). Extracted verbatim from by-date-billing-list.component.ts
 * so all surfaces filter identically. Pure functions — no component state.
 */

/**
 * Get the end date of the current date range, or "now" if there is no date range.
 */
export function getDateRangeEndDate(dateRange: any): moment.Moment {
  if (dateRange && dateRange.endDate) {
    return moment(dateRange.endDate).endOf('day');
  }
  return moment().endOf('day');
}

/**
 * Check if a code date falls within the given date range.
 */
export function isCodeWithinDateRange(codeDate: string, dateRange: any): boolean {
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
    return true; // If no date range, include all codes
  }

  // Convert all dates to UTC for proper comparison
  const codeMoment = moment.utc(codeDate).startOf('day');
  const startDate = moment.utc(dateRange.startDate).startOf('day');
  const endDate = moment.utc(dateRange.endDate).endOf('day');

  return codeMoment.isBetween(startDate, endDate, null, '[]');
}

/**
 * Check if a specific code date falls within any admission period.
 * @param codeDate - The date of the procedure code
 * @param patient - The patient object
 * @param admissionHistory - Optional explicit admission history; falls back to
 *   patient.PatientAdmissionHistories when omitted
 * @param dateRange - Optional date range used to bound the current-inpatient window
 */
export function isCodeDuringAdmission(codeDate: string, patient: any, admissionHistory?: any[], dateRange?: any): boolean {
  const codeMoment = moment.utc(codeDate).startOf('day');

  // If patient is currently inpatient and we have an admission date, check current admission
  if (patient.status === 'inpatient' && patient.admissionDate) {
    const admissionDate = moment.utc(patient.admissionDate).startOf('day');
    const endDate = getDateRangeEndDate(dateRange);

    // Check if code falls within current admission period (admissionDate to endDate)
    return codeMoment.isBetween(admissionDate, endDate, null, '[]');
  }

  // Check admission history for this specific code date
  const history = admissionHistory ?? patient.PatientAdmissionHistories;
  if (history && history.length > 0) {
    return history.some((admission: any) => {
      const admissionDate = moment.utc(admission.admissionDate).startOf('day');
      const dischargeDate = moment.utc(admission.dischargeDate).endOf('day');

      // Check if the code date falls within this admission period
      return codeMoment.isBetween(admissionDate, dischargeDate, null, '[]');
    });
  }

  return false;
}

/**
 * Check if a specific code matches the status filter.
 * @param code - The procedure code object
 * @param codeDate - The date of the procedure code
 * @param patient - The patient object
 * @param statusFilter - The status filter ('inpatient' | 'opd' | 'not_submitted' | 'submitted' | other)
 * @param dateRange - Optional date range narrowing which codes are considered
 * @param admissionHistory - Optional explicit admission history; falls back to
 *   patient.PatientAdmissionHistories when omitted
 */
export function doesCodeMatchStatus(code: any, codeDate: string, patient: any, statusFilter: string, dateRange?: any, admissionHistory?: any[]): boolean {
  // First check if the code falls within the date range
  if (!isCodeWithinDateRange(codeDate, dateRange)) {
    return false;
  }

  if (statusFilter === 'inpatient') {
    // For inpatient filter, check if this specific code falls within an admission period
    return isCodeDuringAdmission(codeDate, patient, admissionHistory, dateRange);
  } else if (statusFilter === 'opd') {
    // For outpatient filter, check if this specific code falls outside any admission period
    return !isCodeDuringAdmission(codeDate, patient, admissionHistory, dateRange);
  } else if (statusFilter === 'not_submitted') {
    return !code.connectProcedureCodeId;
  } else if (statusFilter === 'submitted') {
    return !!code.connectProcedureCodeId;
  } else {
    // For other status filters, use simple status match
    return patient?.status === statusFilter;
  }
}
